/**
 * Pure store↔renderer sync logic for the 3D combat world. No three.js, no
 * zustand: gameStore and WorldCanvas both import from here so the watermark
 * and snapshot rules are unit-testable in isolation.
 */

import type {
  EntityPresentationState,
  GameCharacterInfo,
  PresentationRound,
  WireConditionEntry,
} from '@danda/shared';
import type { EntityVisualHints } from './entities';
import {
  CLASS_CHASSIS,
  CLASS_LOOK,
  type CharAppearance,
  type CharClass,
  type Gender,
} from './char/models';
import { monsterModelFor } from './monsters';

/**
 * Whether a round_result's render transcript should be stashed for playback.
 * The watermark dedupes duplicate deliveries (reconnect drains, dispatcher
 * replays): a round already played never replays. The watermark only
 * advances AFTER playback completes, though, so a duplicate landing
 * mid-playback still clears it. `pendingRoundId` (the currently stashed
 * round, cleared on completion) closes that window: re-stashing the same
 * round as a fresh object would make WorldCanvas cancel and restart it.
 */
export function shouldStashPresentationRound(
  round: PresentationRound | null | undefined,
  lastPlayedRound: number,
  pendingRoundId?: number | null
): round is PresentationRound {
  return round != null && round.round_id > lastPlayedRound && round.round_id !== pendingRoundId;
}

/** Monotonic watermark advance: a stale completion can never regress it. */
export function advancePresentationWatermark(current: number, playedRoundId: number): number {
  return Math.max(current, playedRoundId);
}

/**
 * Watermark value after an authoritative `game_state` snapshot lands.
 *
 * The warmup-release drain sends `game_state` BEFORE the buffered round
 * messages (connection_management.rs::release_warmup_gate), and that snapshot
 * already reflects every buffered round. Its `currentRound` is the drain
 * boundary (the same contract `sessionStartRound` arms from), so advancing the
 * watermark to it keeps the fast-forwarded board from rewinding when the
 * buffered transcripts drain in behind it.
 *
 * Outside warmup, `currentRound` can reference a round still in flight (fresh
 * start R1, mid-round reconnect), so the watermark must NOT advance: that
 * round's transcript still has to play. Session switches start from 0.
 */
export function presentationWatermarkAfterGameState(opts: {
  warmupActive: boolean;
  isSessionSwitch: boolean;
  currentRound: number;
  lastPlayedRound: number;
}): number {
  const carried = opts.isSessionSwitch ? 0 : opts.lastPlayedRound;
  return opts.warmupActive ? Math.max(carried, opts.currentRound) : carried;
}

/**
 * Watermark rollback when a round fails. A warmup failure force-releases the
 * gate, whose `game_state` advanced the watermark to `currentRound` even
 * though the failed round never applied; the backend then RETRIES the same
 * round number live, and its transcript must play. Rolling back to
 * `failedRound - 1` re-opens exactly that round (a no-op when the watermark
 * is already behind it: the normal live-failure case).
 */
export function rollbackPresentationWatermarkForFailedRound(
  current: number,
  failedRound: number
): number {
  return Math.min(current, Math.max(0, failedRound - 1));
}

function conditionName(condition: WireConditionEntry): string {
  return typeof condition === 'string' ? condition : condition.name;
}

type SessionLike = {
  position?: { x?: number; y?: number };
  equipped_slots?: Record<string, string>;
};

/** `session` arrives as an object from the wire but is typed loosely; older
 *  paths may hold a JSON string. Parse defensively, never throw. */
function sessionOf(char: GameCharacterInfo): SessionLike | null {
  const raw: unknown = char.session;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as SessionLike;
    } catch {
      return null;
    }
  }
  return raw as SessionLike;
}

/**
 * Derive a renderer-facing entity snapshot from a `game_state` character
 * list: the reconnect/fast-forward source when no round transcript is in
 * flight. Positions come from the session blob's combat position; characters
 * without one render unpositioned (hidden) until the next transcript places
 * them.
 */
export function worldSnapshotFromGameState(
  characters: GameCharacterInfo[]
): EntityPresentationState[] {
  return characters.map((char) => {
    const session = sessionOf(char);
    const px = session?.position?.x;
    const py = session?.position?.y;
    const pos =
      typeof px === 'number' && typeof py === 'number' && px >= 0 && py >= 0
        ? { x: px, y: py, z: 0 }
        : null;
    const dead = char.isDead === true || char.healthState === 'dead';
    return {
      id: char.charId,
      name: char.name,
      pos,
      hp: char.hp,
      max_hp: char.maxHp,
      alive: !dead,
      conditions: (char.conditions ?? []).map(conditionName),
    };
  });
}

export type WorldFaction = 'party' | 'enemy' | 'neutral';

/** Faction bucket for token coloring; unknown entities read as neutral. */
export function worldFactionFor(
  char: Pick<GameCharacterInfo, 'isPartyMember' | 'attitude' | 'type'> | undefined
): WorldFaction {
  if (!char) return 'neutral';
  if (char.isPartyMember) return 'party';
  if (char.attitude === 'hostile' || char.type === 'monster') return 'enemy';
  return 'neutral';
}

/**
 * Authored body-plan tags shared by asset production and the renderer. Must
 * stay in step with `Morphology` in backend/shared/src/types/asset_manifest.rs
 * (serde snake_case). `serpent` is in the set because Open Adventure ships a
 * giant constrictor snake, even though serpent is cut from the P0 gate.
 */
export const ENTITY_MORPHOLOGIES = [
  'biped',
  'quadruped',
  'winged',
  'serpent',
  'blob',
  'hover',
] as const;
export type EntityMorphology = (typeof ENTITY_MORPHOLOGIES)[number];

/**
 * Defensive read of the authored `morphology` field. The backend is adding it
 * to creature/battle-manifest data; it isn't on the wire types yet, so read
 * through unknown: absent/unknown values fall back to the current visuals.
 */
export function morphologyOf(source: unknown): EntityMorphology | null {
  if (typeof source !== 'object' || source === null) return null;
  const value = (source as Record<string, unknown>).morphology;
  return typeof value === 'string' && (ENTITY_MORPHOLOGIES as readonly string[]).includes(value)
    ? (value as EntityMorphology)
    : null;
}

/** Stable body pick for characters without a recorded binary gender (Q4:
 * 'they' or absent): keyed on charId so the pick never flickers between
 * sessions; the customization UI will let the player choose later. */
export function stableGenderFor(charId: string): Gender {
  let hash = 0;
  for (let i = 0; i < charId.length; i++) hash = (hash * 31 + charId.charCodeAt(i)) | 0;
  return (hash & 1) === 0 ? 'male' : 'female';
}

/** The wire carries full permanent character data as a raw JSON blob
 * (GameCharacterInfo.permanent); gender lives there today. Read loosely. */
function permanentOf(char: GameCharacterInfo): Record<string, unknown> | null {
  const raw = (char as unknown as Record<string, unknown>).permanent;
  return typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : null;
}

/** Pronoun set -> body gender (Q4). he->male, she->female, they/absent ->
 * stable pick (never inferred from the name). Checks a future top-level
 * field first, then the permanent blob (where the engine records it). */
export function bodyGenderFor(char: GameCharacterInfo): Gender {
  const pronoun = (char as unknown as Record<string, unknown>).gender ?? permanentOf(char)?.gender;
  if (pronoun === 'he') return 'male';
  if (pronoun === 'she') return 'female';
  return stableGenderFor(char.charId);
}

const HUMANOID_SPECIES = new Set(['human', 'elf', 'half-elf', 'half elf', 'halfelf']);

function lineageOf(char: GameCharacterInfo): 'human' | 'elf' {
  return (char.species ?? '').toLowerCase().includes('elf') ? 'elf' : 'human';
}

type ItemLike = { item_id?: string; name?: string; item_kind?: string; type?: string };

/** Inventory truth on the wire is `permanent.inventory` (the game wire has
 * no top-level equipment field at session start; `char.equipment` only
 * accumulates mid-session item deltas). Union both by item_id. */
function carriedItemsOf(char: GameCharacterInfo): ItemLike[] {
  const inventory = permanentOf(char)?.inventory;
  const fromBlob = Array.isArray(inventory) ? (inventory as ItemLike[]) : [];
  const fromDeltas = (char.equipment ?? []) as ItemLike[];
  const seen = new Set<string>();
  const out: ItemLike[] = [];
  for (const item of [...fromBlob, ...fromDeltas]) {
    if (typeof item?.name !== 'string' || item.name === '') continue;
    // item_id is always-serialized and may be EMPTY: '' must not collapse
    // every id-less item onto one dedup key.
    const key = item.item_id || `name:${item.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/** Equipped item names: session.equipped_slots ids resolved against the
 * carried items (LLMs and the resolver speak NAMES, not ids). Falls back
 * to every carried weapon-kind item when no slots are recorded.
 * Order is a contract: main_hand first, then off_hand, then the rest.
 * Socket resolution claims one prop per bone first-come, so this order is
 * what makes the MAIN hand win when both hands map to the same bone. */
export function equippedNamesOf(char: GameCharacterInfo): string[] {
  const items = carriedItemsOf(char);
  const slots = sessionOf(char)?.equipped_slots;
  if (slots && typeof slots === 'object') {
    const record = slots as Record<string, unknown>;
    const keys = [
      'main_hand',
      'off_hand',
      ...Object.keys(record).filter((k) => k !== 'main_hand' && k !== 'off_hand'),
    ];
    const ids: string[] = [];
    for (const key of keys) {
      const v = record[key];
      // item_id is always-serialized and may be EMPTY on the wire; an
      // empty slot id must not match an id-less carried item.
      if (typeof v === 'string' && v !== '' && !ids.includes(v)) ids.push(v);
    }
    const names = ids
      .map((id) => items.find((i) => i.item_id === id)?.name)
      .filter((n): n is string => typeof n === 'string');
    if (names.length > 0) return names;
  }
  return items
    .filter((i) => (i.item_kind ?? i.type ?? '').toLowerCase().includes('weapon'))
    .map((i) => i.name as string);
}

/** One body-armor item's look. Plate-family means the FULL plate set;
 * "Half Plate" is medium armor and "Breastplate" is a single chest piece,
 * so both read as the standard armor set (SRD 5.2 armor table). Shields
 * are hand gear and dress nothing. */
function bodyArmorLook(name: string): 'plate' | 'armored' | null {
  const n = name.toLowerCase();
  if (n.includes('shield')) return null;
  const platedFamily =
    n.includes('plate') && !n.includes('half plate') && !n.includes('breastplate');
  return platedFamily ? 'plate' : 'armored';
}

/**
 * Body-armor-driven look (founder 2026-08-02: characters who wear armor
 * keep it in roleplay; only weapons, shields, and helmets strip).
 * Melee-chassis classes only: the other bodies ship no armor part tiers.
 */
function armorLookOf(char: GameCharacterInfo): 'plate' | 'armored' | null {
  const items = carriedItemsOf(char);
  const slots = sessionOf(char)?.equipped_slots;
  // The WORN slot is the truth wherever the session records one: a spare
  // plate in the pack must not re-skin the character, and unequipping
  // must undress him (Unequip leaves the item in inventory).
  if (slots && typeof slots === 'object') {
    const wornId = (slots as { armor?: unknown }).armor;
    if (typeof wornId !== 'string' || wornId === '') return null;
    const worn = items.find((i) => i.item_id === wornId);
    // Worn but unresolvable id: something IS on the body, tier unknown.
    return typeof worn?.name === 'string' ? bodyArmorLook(worn.name) : 'armored';
  }
  // No slot record (module fixtures, NPC casts): those casts ship exactly
  // what they wear, so every carried body armor counts.
  const looks = items
    .filter((i) => (i.item_kind ?? i.type ?? '').toLowerCase().includes('armor'))
    .map((i) => bodyArmorLook(i.name as string))
    .filter((look): look is 'plate' | 'armored' => look !== null);
  if (looks.length === 0) return null;
  return looks.includes('plate') ? 'plate' : 'armored';
}

/**
 * Wire appearance block (backend `CharacterAppearance`, camelCase; PRD
 * `docs/specs/character-appearance.md`). Body axes apply to the renderer
 * verbatim; the color fields map to tint slots in `appearanceTints`.
 */
interface WireAppearance {
  look?: string;
  /** Body family for classless NPCs ('melee' | 'ranged' | 'caster'):
   * e.g. the commoner look on the mage body authors chassis 'caster'. */
  chassis?: string;
  outfitSet?: number;
  helmet?: number;
  cosmetics?: string[];
  hairColor?: string;
  hairStyle?: number;
  skinTone?: string;
  mainColor?: string;
  armorColor?: string;
  bannerColor?: string;
  bodyType?: string;
  /** Direct renderer tints (module-authored NPC looks); win over derived. */
  tints?: Record<string, string>;
}

function appearanceBlockOf(source: unknown): Partial<WireAppearance> | null {
  if (typeof source !== 'object' || source === null) return null;
  const value = (source as Record<string, unknown>).appearance;
  return typeof value === 'object' && value !== null ? (value as Partial<WireAppearance>) : null;
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Mirrors `backend/shared/src/types/character/appearance.rs` palettes:
 * creation seeding uses the Rust copies, the render edge mirrors them so
 * an ABSENT block yields the same stable look with zero migration. */
const SEED_HAIR_PALETTE = [
  '#e3c96f',
  '#c9a85c',
  '#8a4b2a',
  '#b06a38',
  '#6b4a2e',
  '#4a3826',
  '#241c13',
  '#7d7468',
];
const SEED_MAIN_PALETTE = [
  '#2b3f6b',
  '#4f8f45',
  '#9c3230',
  '#c9924a',
  '#6b4f96',
  '#2f8a8f',
  '#d8cfa8',
  '#3c3a33',
];
/** Rust `BANNER_PALETTE`, same order. The backend walks it from the
 * seeded index to dodge hues a party already flies; the render edge has
 * no party context, so it takes the seeded index outright, which is what
 * `seeded_for` yields for an empty `taken_banners`. */
const SEED_BANNER_PALETTE = [
  '#e0b341',
  '#c4452e',
  '#3a7bc4',
  '#3f9b57',
  '#8a4bc4',
  '#d97b2e',
  '#2fa7a0',
  '#c44b8a',
];

/** Stable palette pick per character (same convention as the gender
 * stable-pick); `salt` keeps hair and cloth picks independent. */
export function stablePaletteIndex(charId: string, salt: number, length: number): number {
  let hash = salt;
  for (let i = 0; i < charId.length; i++) hash = (hash * 31 + charId.charCodeAt(i)) | 0;
  return Math.abs(hash) % length;
}

function shadeHex(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const channel = (v: number) => Math.max(0, Math.min(255, Math.round(v * factor)));
  const shaded =
    (channel((n >> 16) & 255) << 16) | (channel((n >> 8) & 255) << 8) | channel(n & 255);
  return `#${shaded.toString(16).padStart(6, '0')}`;
}

/**
 * Schema colors -> renderer tint slots. Rulings applied: main color tints
 * cloth on EVERY chassis (derived B/C shades keep depth); armor color maps
 * to armorA-C; banner tints the shield emblem. Absent colors seed from the
 * palettes (hair) and `favorite_color` (main), so every character gets a
 * distinct stable look with no authored data. The banner is the one
 * exception: it means PARTY identity, so only a party member seeds one.
 */
export function appearanceTints(
  char: GameCharacterInfo,
  authored: Partial<WireAppearance> | null
): Record<string, string> {
  const tints: Record<string, string> = {};
  const favorite = permanentOf(char)?.favorite_color;
  const main =
    authored?.mainColor ??
    (typeof favorite === 'string' && HEX_COLOR.test(favorite)
      ? favorite
      : SEED_MAIN_PALETTE[stablePaletteIndex(char.charId, 7, SEED_MAIN_PALETTE.length)]);
  tints.hair =
    authored?.hairColor ??
    SEED_HAIR_PALETTE[stablePaletteIndex(char.charId, 13, SEED_HAIR_PALETTE.length)];
  tints.clothA = main;
  tints.clothB = shadeHex(main, 0.82);
  tints.clothC = shadeHex(main, 0.66);
  if (authored?.skinTone) tints.skin = authored.skinTone;
  // Armor follows the identity color too, darkened toward metal. Left
  // untinted, every armored character wore the pack's one default plate,
  // so a party read as a uniformed squad however different their cloth
  // was (founder 2026-08-02: "way too similar"). An authored armorColor
  // still wins outright.
  const armor = authored?.armorColor ?? shadeHex(main, 0.62);
  tints.armorA = armor;
  tints.armorB = shadeHex(armor, 0.85);
  tints.armorC = shadeHex(armor, 0.7);
  // Banner is PARTY IDENTITY, so only a party member gets a seeded one.
  // A member saved before the appearance schema has no block at all, and
  // a bare `if (authored?.bannerColor)` left its name pill unedged, its
  // bubble unbordered, and its shield white while every other color slot
  // had a stable fallback. Salt 29 is the one `seeded_for` uses, so the
  // hue matches what the backend would have assigned.
  //
  // Seeding it for everyone would be worse than the gap it closes:
  // `appearanceTints` runs for every humanoid, and the plate falls back to
  // the FACTION color when there is no banner (`overlay.ts`). A seeded
  // NPC banner would put a party-palette hue on a bandit's pill and bubble
  // and drop the faction cue, so a hostile could fly a party member's
  // exact hue. An AUTHORED banner still wins for anyone, since a module
  // that names one means it.
  const seededBanner = char.isPartyMember
    ? SEED_BANNER_PALETTE[stablePaletteIndex(char.charId, 29, SEED_BANNER_PALETTE.length)]
    : undefined;
  const banner = authored?.bannerColor ?? seededBanner;
  if (banner) tints.emblem = banner;
  return { ...tints, ...(authored?.tints ?? {}) };
}

/**
 * Hero appearance for a character record, when the record IS a hero-model
 * candidate: every party member, and humanoid NPCs (authored appearance
 * block or human/elf species). Monsters and animals return null.
 */
export function heroAppearanceFor(char: GameCharacterInfo): CharAppearance | null {
  if (char.type === 'monster' || char.type === 'animal') return null;
  const authored = appearanceBlockOf(char);
  const klass = (char.class ?? '').toLowerCase() as CharClass;
  const hasClass = klass in CLASS_CHASSIS;
  const species = (char.species ?? '').toLowerCase();
  const humanoid = hasClass || !!authored || species === '' || HUMANOID_SPECIES.has(species);
  if (!humanoid) return null;
  // The authored block passes through wholesale (module NPC looks speak
  // renderer vocabulary: chassis, gender, look, sets), MINUS the schema
  // color keys, which land as tint slots via appearanceTints below.
  const bodyAxes = { ...(authored as Partial<CharAppearance> | null) };
  for (const key of [
    'hairColor',
    'hairStyle',
    'skinTone',
    'mainColor',
    'armorColor',
    'bannerColor',
    'bodyType',
    'tints',
  ]) {
    delete (bodyAxes as Record<string, unknown>)[key];
  }
  // Worn body armor refines the melee look (plate set or armor set);
  // authored looks still win via the bodyAxes spread below. Classes that
  // own a body preset are exempt: overriding a cleric to 'armored' costs
  // him his cast clips, his mace, and his bare head, so Maren would swing
  // a sword while casting Cure Wounds.
  const armorLook =
    hasClass && CLASS_CHASSIS[klass] === 'melee' && !CLASS_LOOK[klass] ? armorLookOf(char) : null;
  return {
    ...(hasClass ? { class: klass } : { chassis: 'melee' as const, look: 'standard' as const }),
    ...(armorLook ? { look: armorLook } : {}),
    lineage: lineageOf(char),
    gender: bodyGenderFor(char),
    equipment: equippedNamesOf(char),
    // Classless NPCs stay unarmed unless hostile (bandits draw steel;
    // villagers don't).
    defaultLoadout: hasClass || char.attitude === 'hostile',
    ...bodyAxes,
    tints: appearanceTints(char, authored),
  };
}

/**
 * Production entity-visual resolution: faction coloring, hero models for
 * party members + humanoid NPCs (workstream A), registered Meshy models
 * for monsters, morphology-driven bodies (blob), primitive proxy for the
 * rest. The marker ring remains the loading placeholder and the visible
 * degrade when a model can't load. `morphology` may ride the presentation
 * snapshot or the character record; check both.
 */
/** Grid footprint (w, h) from the wire permanent: explicit module override
 * or the 5E square from creature size. Mirrors the engine's
 * `CharacterData::footprint_wh` so visuals center on the same cells the
 * mechanics reserve. */
function footprintOf(char: GameCharacterInfo | undefined): { w: number; h: number } {
  const perm = char ? permanentOf(char) : undefined;
  const fp = (perm as { footprint?: { w?: number; h?: number } } | undefined)?.footprint;
  if (fp && (fp.w ?? 0) >= 1 && (fp.h ?? 0) >= 1) return { w: fp.w!, h: fp.h! };
  const size = String((perm as { size?: string } | undefined)?.size ?? '').toLowerCase();
  const n = size === 'large' ? 2 : size === 'huge' ? 3 : size === 'gargantuan' ? 4 : 1;
  return { w: n, h: n };
}

export function worldVisualHintsFor(
  entity: EntityPresentationState,
  char: GameCharacterInfo | undefined
): EntityVisualHints {
  const faction = worldFactionFor(char);
  const footprint = footprintOf(char);
  const morphology = morphologyOf(entity) ?? morphologyOf(char);
  if (morphology === 'blob' && faction !== 'party') {
    return { faction, markerOnly: false, blob: {}, footprint };
  }
  const hero = char ? heroAppearanceFor(char) : null;
  if (hero) return { faction, markerOnly: faction === 'party', hero, footprint };
  const monster = monsterModelFor(char?.name ?? entity.name) ?? monsterModelFor(entity.id);
  if (monster) {
    return {
      faction,
      markerOnly: false,
      clips: monster.clips,
      model: monster.model,
      scale: monster.scale,
      footprint,
    };
  }
  return { faction, markerOnly: faction === 'party', footprint };
}
